import React, { PropsWithChildren, useEffect, useState } from 'react';
import { motion, useAnimation, Variants } from 'framer-motion';

import IconChevron from '@/assets/icons/heroIcons/Chevron-down.svg';
import { mergeClasses } from '@/utils/style';

interface DocumentSectionProps {
	title: string | React.ReactElement;
	icon?: React.ReactElement;
	id?: string;
	defaultOpen?: boolean;
	isDropdown?: boolean;
	isOpen?: boolean;
	onOpen?: () => void;
	onClose?: () => void;
	dropdownClassName?: string;
	smallArrow?: boolean;
}

export const DocumentSection = (props: PropsWithChildren<DocumentSectionProps>) => {
	const {
		isOpen: externalIsOpen,
		onOpen,
		onClose,
		title,
		icon,
		id,
		defaultOpen = false,
		isDropdown = true,
		children,
		dropdownClassName,
	} = props;

	const [isOpen, setIsOpen] = useState(externalIsOpen ?? defaultOpen);
	const controls = useAnimation();

	useEffect(() => {
		if (externalIsOpen !== undefined) {
			setIsOpen(externalIsOpen);
			controls.start(externalIsOpen ? 'open' : 'closed');
		}
	}, [externalIsOpen, controls]);

	const toggleDropdown = () => {
		const newIsOpen = !isOpen;
		setIsOpen(newIsOpen);
		controls.start(newIsOpen ? 'open' : 'closed');
		if (newIsOpen && onOpen) {
			onOpen();
		} else if (!newIsOpen && onClose) {
			onClose();
		}
	};

	const variants: Variants = {
		open: { height: 'auto', opacity: 1, display: 'block' },
		closed: { height: 0, opacity: 0, display: 'none' },
	};

	function renderContent() {
		return <div className="pt-6">{children}</div>;
	}

	return (
		<div className="rounded-2xl bg-white px-4 py-6 lg:p-8" id={id}>
			<button
				type="button"
				className="flex w-full flex-row items-center gap-4 text-left lg:gap-6"
				onClick={toggleDropdown}
				aria-expanded={isOpen}
			>
				{icon && <div className="shrink-0">{icon}</div>}
				<span className="w-full text-left text-lg font-bold lg:text-4xl">{title}</span>
				{isDropdown && (
					<div
						className={mergeClasses(
							'shrink-0 transform transition-transform duration-300',
							isOpen ? 'rotate-180' : 'rotate-0'
						)}
					>
						<IconChevron
							className={props.smallArrow ? 'h-6 w-6' : 'h-10 w-10'}
							aria-hidden="true"
						/>
					</div>
				)}
			</button>
			{isDropdown ? (
				<motion.div
					className={mergeClasses('overflow-hidden', dropdownClassName)}
					animate={controls}
					initial={isOpen ? 'open' : 'closed'}
					variants={variants}
				>
					{renderContent()}
				</motion.div>
			) : (
				renderContent()
			)}
		</div>
	);
};
